/**
 * Second `entity.clientGet` fetch, this time surfacing the WIDER row the
 * first-run setup screen needs (country, registration_no, address) —
 * `useSessionFacts()` narrows the same call's response down to just
 * `name`/`currency_code`/`financial_year_end` for the settings adapter, so
 * this hook exists to give the first-run screen access to the fields that
 * narrowing throws away, WITHOUT widening `RealOrganisationRecord` and
 * dragging the settings-save path along with it (issue #23952).
 *
 * A single extra round trip on the setup screen only — the screen is rendered
 * at most once per organisation-per-user (after "Set up payroll" succeeds the
 * app lands on the dashboard and never re-mounts this).
 *
 * Same client (`getTrpcClient()`, cookie-based) as `useSessionFacts()`.
 */
import * as React from 'react';
import { getTrpcClient } from '../lib/trpc.js';
import { useOAuthEntityContext } from '../lib/auth/oauth_entity_context.js';
import type {
  EntityDetails,
  EntityAddressJson,
} from '../screens/setup/first_run_setup_logic.js';

export type EntityClientDetailsState =
  | { status: 'loading' }
  | { status: 'no_entity' }
  | { status: 'error' }
  | { status: 'ready'; entity: EntityDetails };

export interface UseEntityClientDetailsResult {
  state: EntityClientDetailsState;
  retry: () => void;
}

/**
 * `entities.address` is stored as `text('address', { mode: 'json' })` — the
 * tRPC output type is `unknown`. Narrow it here at the boundary so callers
 * (and `first_run_setup_logic.ts`) can trust the shape without another
 * duck-type check.
 */
function narrowAddress(value: unknown): EntityAddressJson | null {
  if (typeof value !== 'object' || value === null) return null;
  const obj = value as Record<string, unknown>;
  const pick = (key: string): string | undefined =>
    typeof obj[key] === 'string' ? obj[key] : undefined;
  return {
    line1: pick('line1'),
    line2: pick('line2'),
    city: pick('city'),
    state: pick('state'),
    postal_code: pick('postal_code'),
    country: pick('country'),
    attention: pick('attention'),
  };
}

export function useEntityClientDetails(): UseEntityClientDetailsResult {
  const entityContext = useOAuthEntityContext();
  const [entity, setEntity] = React.useState<EntityDetails | undefined>(
    undefined,
  );
  const [failed, setFailed] = React.useState(false);
  const [reloadToken, setReloadToken] = React.useState(0);

  React.useEffect(() => {
    if (entityContext.status !== 'ready') return;
    const entityId = entityContext.entity_id;
    let cancelled = false;
    setEntity(undefined);
    setFailed(false);

    async function load(): Promise<void> {
      try {
        const client = getTrpcClient();
        const result = await client.entity.clientGet.query({
          entity_id: entityId,
        });
        if (cancelled) return;
        setEntity({
          name: result.entity.name,
          country: result.entity.country ?? null,
          currency_code: result.entity.currency_code,
          financial_year_end: result.entity.financial_year_end,
          registration_no: result.entity.registration_no ?? null,
          address: narrowAddress(result.entity.address),
        });
      } catch (err) {
        if (cancelled) return;
        console.error(
          '[wagepro-web] Failed to resolve entity details for setup screen:',
          err,
        );
        setFailed(true);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [entityContext, reloadToken]);

  const retry = React.useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  const state: EntityClientDetailsState =
    entityContext.status === 'no_entity'
      ? { status: 'no_entity' }
      : entity !== undefined
        ? { status: 'ready', entity }
        : failed
          ? { status: 'error' }
          : { status: 'loading' };

  return { state, retry };
}
